import { css } from '@firebolt-dev/css'
import { clamp } from 'lodash-es'
import { useEffect, useMemo, useRef, useState } from 'react'

import { cls } from '../core/cls'
import { FlipHorizontal2Icon, FlipVertical2Icon, RotateCcwIcon, Maximize2Icon } from 'lucide-react'

const ZOOM_SPEED = 0.003
const ZOOM_MIN = 2
const ZOOM_MAX = 40
const LMB = 1
const RMB = 2
const IN_HANG = 100

const rotationLabels = ['', '90', '180', '270']

export function UIEditorTextureWindow({ history }) {
  const containerRef = useRef()
  const pageRef = useRef()
  const contentRef = useRef()
  const gridRef = useRef()

  const { asset, texture, ui } = history.data
  const canvas = texture.canvas

  const node = ui.nodeId ? findNode(asset.model.root.children, ui.nodeId) : null

  const faces = useFaces(node)

  const [zoom, setZoom] = useState(3)
  const [selectedFaceId, setSelectedFaceId] = useState(null)

  const infoRef = useRef({
    drawing: false,
    drawIdx: -1,
    panning: false,
    x: 0,
    y: 0,
  })

  const selectedFace = selectedFaceId ? faces.find(f => f.id === selectedFaceId) : null

  const getPadding = () => {
    const page = pageRef.current
    const cs = getComputedStyle(page)
    return {
      l: parseFloat(cs.paddingLeft) || 0,
      r: parseFloat(cs.paddingRight) || 0,
      t: parseFloat(cs.paddingTop) || 0,
      b: parseFloat(cs.paddingBottom) || 0,
    }
  }

  const getWorldXY = e => {
    const info = infoRef.current
    const crect = containerRef.current.getBoundingClientRect()
    const cursorX = e.clientX - crect.left
    const cursorY = e.clientY - crect.top
    const pad = getPadding()
    const currentZoom = contentRef.current.offsetWidth / canvas.width
    const worldX = (cursorX - info.x - pad.l) / currentZoom
    const worldY = (cursorY - info.y - pad.t) / currentZoom
    return { worldX, worldY }
  }

  const getPixelIndex = e => {
    const { worldX, worldY } = getWorldXY(e)
    const ix = Math.floor(worldX)
    const iy = Math.floor(worldY)
    if (ix < 0 || iy < 0 || ix >= canvas.width || iy >= canvas.height) return -1
    const index = (iy * canvas.width + ix) * 4
    return index
  }

  const draw = (e, start) => {
    history.edit(data => {
      const texture = data.texture
      const info = infoRef.current
      const idx = getPixelIndex(e)
      if (idx === -1) return
      if (info.drawIdx === idx) return
      info.drawIdx = idx
      const rgba = ui.paintMode === 'draw' ? hexToRgba(ui.color) : [0, 0, 0, 0]
      if (start) {
        texture.imageData = new ImageData(new Uint8ClampedArray(texture.imageData.data), texture.width, texture.height)
      }
      texture.imageData.data[idx + 0] = rgba[0]
      texture.imageData.data[idx + 1] = rgba[1]
      texture.imageData.data[idx + 2] = rgba[2]
      texture.imageData.data[idx + 3] = rgba[3]
      texture.version++
    })
  }

  const drawEnd = () => {
    history.commit()
  }

  const updatePan = () => {
    const info = infoRef.current
    const container = containerRef.current
    const page = pageRef.current
    const content = contentRef.current
    const pad = getPadding()
    const contW = container.offsetWidth
    const contH = container.offsetHeight
    const cW = content.offsetWidth
    const cH = content.offsetHeight
    const minX = IN_HANG - cW - pad.l
    const maxX = contW - IN_HANG - pad.l
    const minY = IN_HANG - cH - pad.t
    const maxY = contH - IN_HANG - pad.t
    info.x = minX <= maxX ? clamp(info.x, minX, maxX) : (minX + maxX) / 2
    info.y = minY <= maxY ? clamp(info.y, minY, maxY) : (minY + maxY) / 2
    page.style.left = info.x + 'px'
    page.style.top = info.y + 'px'
  }

  // Update face position (x/y)
  const updateFacePosition = (faceName, newY, newX) => {
    history.edit(data => {
      const node = findNode(data.asset.model.root.children, ui.nodeId)
      const faceData = node.faces[faceName]
      faceData.y = newY
      faceData.x = newX
      data.ui.version++
    })
  }

  // Rotate face 90° clockwise
  const rotateFace90CW = faceId => {
    const face = faces.find(f => f.id === faceId)
    if (!face) return

    history.edit(data => {
      const node = findNode(data.asset.model.root.children, ui.nodeId)
      const faceData = node.faces[face.name]

      // Increment rotation (0 -> 1 -> 2 -> 3 -> 0)
      faceData.rotation = ((faceData.rotation || 0) + 1) % 4
      data.ui.version++
    })
    history.commit()
  }

  // Flip horizontal - toggle flipX boolean
  const flipFaceX = faceId => {
    const face = faces.find(f => f.id === faceId)
    if (!face) return

    history.edit(data => {
      const node = findNode(data.asset.model.root.children, ui.nodeId)
      const faceData = node.faces[face.name]
      faceData.flipX = !faceData.flipX
      data.ui.version++
    })
    history.commit()
  }

  // Flip vertical - toggle flipY boolean
  const flipFaceY = faceId => {
    const face = faces.find(f => f.id === faceId)
    if (!face) return

    history.edit(data => {
      const node = findNode(data.asset.model.root.children, ui.nodeId)
      const faceData = node.faces[face.name]
      faceData.flipY = !faceData.flipY
      data.ui.version++
    })
    history.commit()
  }

  // Toggle between 1x and 2x density
  const toggleFaceDensity = faceId => {
    const face = faces.find(f => f.id === faceId)
    if (!face) return

    history.edit(data => {
      const node = findNode(data.asset.model.root.children, ui.nodeId)
      const faceData = node.faces[face.name]

      const currentDensity = faceData.density || 1
      const newDensity = currentDensity === 1 ? 2 : 1

      // Get the rendered dimensions to check bounds
      const baseSize = getBaseFaceSize(node, face.name)
      const rotation = faceData.rotation || 0
      const isRotated = rotation === 1 || rotation === 3

      const newW = (isRotated ? baseSize.h : baseSize.w) * newDensity
      const newH = (isRotated ? baseSize.w : baseSize.h) * newDensity

      // Clamp position to keep face within texture bounds
      const maxX = canvas.width - newW
      const maxY = canvas.height - newH

      faceData.x = clamp(faceData.x || 0, 0, Math.max(0, maxX))
      faceData.y = clamp(faceData.y || 0, 0, Math.max(0, maxY))
      faceData.density = newDensity

      data.ui.version++
    })
    history.commit()
  }

  useEffect(() => {
    contentRef.current.appendChild(canvas)
    const grid = createGrid(canvas.width, canvas.height)
    contentRef.current.appendChild(grid.svg)
    gridRef.current = grid
  }, [canvas])

  useEffect(() => {
    const container = containerRef.current
    const page = pageRef.current
    const content = contentRef.current
    const cWidth = canvas.width * zoom
    const cHeight = canvas.height * zoom
    content.style.width = `${cWidth}px`
    content.style.height = `${cHeight}px`
    page.style.paddingLeft = `${container.offsetWidth - cWidth / 2}px`
    page.style.paddingRight = `${container.offsetWidth - cWidth / 2}px`
    page.style.paddingTop = `${container.offsetHeight - cHeight / 2}px`
    page.style.paddingBottom = `${container.offsetHeight - cHeight / 2}px`
    let thickness = clamp(zoom * 0.02, 0, 0.5)
    if (thickness < 0.1) thickness = 0
    gridRef.current.setThickness(thickness)
  }, [zoom, canvas.width, canvas.height])

  useEffect(() => {
    const info = infoRef.current
    const container = containerRef.current
    info.x = -container.offsetWidth / 2
    info.y = -container.offsetHeight / 2
    updatePan()
  }, [canvas])

  return (
    <div
      ref={containerRef}
      className='studio-texture'
      css={css`
        position: relative;
        height: 100%;
        border-bottom: 1px solid #151519;
        border-radius: 10px;
        overflow: hidden;
        .studio-texture-page {
          position: absolute;
          top: 0;
          left: 0;
        }
        .studio-texture-content {
          border: 1px solid #2a2a33;
          position: relative;
        }
        canvas {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-position: 0px 0px;
          object-fit: fill;
          image-rendering: pixelated;
          image-rendering: crisp-edges;
        }
        > svg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-position: 0px 0px;
          object-fit: fill;
        }
        .studio-texture-face {
          position: absolute;
          border: 1.5px solid white;
          z-index: 1;
          pointer-events: none;
          box-sizing: border-box;
          &.active {
            pointer-events: auto;
            cursor: grab;
          }
          &.selected {
            border-color: #006bc3;
            border-width: 2px;
          }
          &.dragging {
            cursor: grabbing;
          }
          .face-label {
            position: absolute;
            top: -18px;
            left: 0;
            font-size: 11px;
            color: white;
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
            pointer-events: none;
            white-space: nowrap;
          }
        }
        .face-btns {
          position: absolute;
          display: flex;
          z-index: 10;
          pointer-events: auto;
          background: var(--bg3);
          border-radius: 10px;
          padding: 4px;
          &-btn {
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 600;
            color: #ccc;
            margin-right: 2px;
            position: relative;
            > span {
              position: absolute;
              bottom: 0;
              right: 0;
              font-size: 8px;
            }
            &:last-child {
              margin-right: 0;
            }
            &:hover {
              cursor: pointer;
              background: var(--bg4);
            }
            &.active {
              background: var(--bg5);
              color: white;
            }
          }
        }
      `}
      onPointerDown={e => {
        const info = infoRef.current
        const left = !!(e.buttons & LMB)
        if (left && (ui.paintMode === 'draw' || ui.paintMode === 'erase')) {
          containerRef.current.setPointerCapture(e.pointerId)
          info.drawing = true
          info.drawIdx = -1
          draw(e, true)
        }
        if (left && ui.paintMode === 'face' && !e.didHitFace) {
          setSelectedFaceId(null)
        }
        const right = !!(e.buttons & RMB)
        if (right) {
          containerRef.current.setPointerCapture(e.pointerId)
          info.panning = true
        }
      }}
      onPointerMove={e => {
        const info = infoRef.current
        if (info.drawing) {
          draw(e)
        }
        if (info.panning) {
          info.x += e.movementX
          info.y += e.movementY
          updatePan()
        }
      }}
      onPointerUp={e => {
        const info = infoRef.current
        const container = containerRef.current
        if (container.hasPointerCapture?.(e.pointerId)) {
          container.releasePointerCapture(e.pointerId)
        }
        const left = !!(e.buttons & LMB)
        if (info.drawing && !left) {
          info.drawing = false
          drawEnd()
        }
        const right = !!(e.buttons & RMB)
        if (info.panning && !right) {
          info.panning = false
        }
      }}
      onLostPointerCapture={e => {
        const info = infoRef.current
        if (info.panning) {
          info.panning = false
        }
        if (info.drawing) {
          info.drawing = false
          drawEnd()
        }
      }}
      onWheel={e => {
        const info = infoRef.current
        const container = containerRef.current
        const page = pageRef.current

        const crect = container.getBoundingClientRect()
        const cursorX = e.clientX - crect.left
        const cursorY = e.clientY - crect.top

        const pad0 = getPadding()
        const worldX = (cursorX - info.x - pad0.l) / zoom
        const worldY = (cursorY - info.y - pad0.t) / zoom

        const delta = e.shiftKey ? e.deltaX : e.deltaY
        const factor = Math.exp(-delta * ZOOM_SPEED)
        let nextZoom = clamp(zoom * factor, ZOOM_MIN, ZOOM_MAX)

        const cWidth = canvas.width * nextZoom
        const cHeight = canvas.height * nextZoom
        const padL = Math.max(0, container.offsetWidth - cWidth / 2)
        const padT = Math.max(0, container.offsetHeight - cHeight / 2)

        info.x = cursorX - padL - worldX * nextZoom
        info.y = cursorY - padT - worldY * nextZoom

        const content = contentRef.current
        content.style.width = `${cWidth}px`
        content.style.height = `${cHeight}px`
        page.style.paddingLeft = `${padL}px`
        page.style.paddingRight = `${padL}px`
        page.style.paddingTop = `${padT}px`
        page.style.paddingBottom = `${padT}px`

        setZoom(nextZoom)
        updatePan()
      }}
      onContextMenu={e => {
        e.preventDefault()
      }}
    >
      <div ref={pageRef} className='studio-texture-page'>
        <div ref={contentRef} className='studio-texture-content'>
          {faces.map(face => {
            const isSelected = selectedFaceId === face.id
            const isDragging = infoRef.current.faceId === face.id

            return (
              <div
                key={face.id}
                className={cls('studio-texture-face', {
                  selected: isSelected,
                  active: ui.paintMode === 'face',
                  dragging: isDragging,
                })}
                style={{
                  top: face.y * zoom,
                  left: face.x * zoom,
                  width: face.width * zoom,
                  height: face.height * zoom,
                }}
                onPointerDown={e => {
                  if (ui.paintMode !== 'face') return
                  const left = !!(e.buttons & LMB)
                  if (!left) return

                  e.didHitFace = true
                  e.stopPropagation()
                  setSelectedFaceId(face.id)

                  const info = infoRef.current
                  info.faceId = face.id
                  e.target.setPointerCapture(e.pointerId)

                  const { worldX, worldY } = getWorldXY(e)
                  info.startY = face.y
                  info.startX = face.x
                  info.grabX = worldX
                  info.grabY = worldY
                  info.faceWidth = face.width
                  info.faceHeight = face.height
                  info.raf = null
                  info.pending = null
                }}
                onPointerMove={e => {
                  if (ui.paintMode !== 'face') return
                  const info = infoRef.current
                  if (info.faceId !== face.id) return

                  const { worldX, worldY } = getWorldXY(e)
                  const dx = worldX - info.grabX
                  const dy = worldY - info.grabY

                  let newX = info.startX + dx
                  let newY = info.startY + dy

                  // Constrain to texture bounds
                  newX = clamp(newX, 0, canvas.width - info.faceWidth)
                  newY = clamp(newY, 0, canvas.height - info.faceHeight)

                  const px = Math.round(newX)
                  const py = Math.round(newY)

                  info.pending = { name: face.name, y: py, x: px }
                  if (!info.raf) {
                    info.raf = requestAnimationFrame(() => {
                      const p = info.pending
                      info.raf = null
                      if (!p) return
                      updateFacePosition(p.name, p.y, p.x)
                    })
                  }
                }}
                onPointerUp={e => {
                  if (ui.paintMode !== 'face') return
                  if (e.target.hasPointerCapture?.(e.pointerId)) {
                    e.target.releasePointerCapture(e.pointerId)
                  }
                  const info = infoRef.current
                  if (info.faceId !== face.id) return
                  info.faceId = null
                  history.commit()
                }}
                onLostPointerCapture={e => {
                  if (ui.paintMode !== 'face') return
                  const info = infoRef.current
                  if (info.faceId !== face.id) return
                  info.faceId = null
                  history.commit()
                }}
              >
                {ui.paintMode === 'face' && <span className='face-label'>{face.name}</span>}

                {/* Transform controls (only when selected) */}
                {isSelected && ui.paintMode === 'face' && (
                  <div
                    className='face-btns'
                    style={{
                      left: 0,
                      top: face.height * zoom + 6,
                    }}
                  >
                    <div className='face-btns-btn' onClick={() => toggleFaceDensity(face.id)}>
                      {face.density === 1 ? '1x' : '2x'}
                    </div>
                    <div className='face-btns-btn' onClick={() => rotateFace90CW(face.id)}>
                      <RotateCcwIcon size={18} />
                      <span>{rotationLabels[face.rotation]}</span>
                    </div>
                    <div className={cls('face-btns-btn', { active: face.flipX })} onClick={() => flipFaceX(face.id)}>
                      <FlipHorizontal2Icon size={18} />
                    </div>
                    <div className={cls('face-btns-btn', { active: face.flipY })} onClick={() => flipFaceY(face.id)}>
                      <FlipVertical2Icon size={18} />
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function createGrid(width, height, step = 1) {
  const NS = 'http://www.w3.org/2000/svg'

  const svg = document.createElementNS(NS, 'svg')
  svg.setAttribute('xmlns', NS)
  svg.setAttribute('width', String(width))
  svg.setAttribute('height', String(height))
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`)

  const path = document.createElementNS(NS, 'path')
  path.setAttribute('fill', 'none')
  path.setAttribute('stroke', '#3d414a')
  path.setAttribute('stroke-width', '1px')
  path.setAttribute('vector-effect', 'non-scaling-stroke')

  const parts = []
  for (let x = 0; x <= width; x += step) {
    parts.push(`M${x} 0V${height}`)
  }
  for (let y = 0; y <= height; y += step) {
    parts.push(`M0 ${y}H${width}`)
  }
  path.setAttribute('d', parts.join(' '))
  svg.appendChild(path)
  return {
    svg,
    setThickness: value => {
      path.setAttribute('stroke-width', `${value}px`)
    },
  }
}

// Get the base (1x) size for a face based on box dimensions
function getBaseFaceSize(node, faceName) {
  const [sx, sy, sz] = node.size.map(n => Math.abs(n))
  switch (faceName) {
    case 'up':
    case 'down':
      return { w: sx, h: sz }
    case 'north':
    case 'south':
      return { w: sx, h: sy }
    case 'east':
    case 'west':
      return { w: sz, h: sy }
    default:
      return { w: 1, h: 1 }
  }
}

function useFaces(node) {
  const faces = useMemo(() => {
    const faces = []
    if (!node) return faces

    function add(node, name) {
      const f = node.faces[name]
      // Check if face has the new properties (at minimum, x and y should exist)
      if (f.x === undefined || f.y === undefined) return

      const rotation = f.rotation || 0
      const density = f.density || 1
      const flipX = f.flipX || false
      const flipY = f.flipY || false

      // Get base size for this face
      const baseSize = getBaseFaceSize(node, name)

      // If rotated 90° or 270°, swap width and height
      const isRotated = rotation === 1 || rotation === 3
      const width = (isRotated ? baseSize.h : baseSize.w) * density
      const height = (isRotated ? baseSize.w : baseSize.h) * density

      faces.push({
        id: `${node.id}_${name}`,
        nodeId: node.id,
        name,
        y: f.y,
        x: f.x,
        width,
        height,
        rotation,
        density,
        flipX,
        flipY,
      })
    }

    function traverse(nodes) {
      for (const node of nodes) {
        if (node.type === 'box') {
          add(node, 'up')
          add(node, 'down')
          add(node, 'north')
          add(node, 'south')
          add(node, 'east')
          add(node, 'west')
        }
        traverse(node.children)
      }
    }
    traverse([node])
    return faces
  }, [node])
  return faces
}

function hexToRgba(hex) {
  hex = hex.replace(/^#/, '')

  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(c => c + c)
      .join('')
  } else if (hex.length === 4) {
    hex = hex
      .split('')
      .map(c => c + c)
      .join('')
  }

  const bigint = parseInt(hex, 16)
  let r, g, b, a

  if (hex.length === 6) {
    r = (bigint >> 16) & 255
    g = (bigint >> 8) & 255
    b = bigint & 255
    a = 255
  } else if (hex.length === 8) {
    r = (bigint >> 24) & 255
    g = (bigint >> 16) & 255
    b = (bigint >> 8) & 255
    a = bigint & 255
  } else {
    throw new Error('Invalid hex color')
  }

  return [r, g, b, a]
}

function findNode(nodes, id) {
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]
    if (node.id === id) return node
    if (node.children.length) {
      const match = findNode(node.children, id)
      if (match) return match
    }
  }
  return null
}
